import { useQuery } from '@tanstack/react-query';
import { CollapseProps, Descriptions, Divider, Flex, Grid, Skeleton, Typography } from 'antd';
import { Fragment, useContext, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getEquipmentSupplyRequestTicket } from 'src/apis/hr_ticket/equipmentSupplyRequestTicket.api';
import { BtnHrHandle } from 'src/components/v2/Button';
import CollapseCustom from 'src/components/v2/Collapse';
import PriorityLevelTag from 'src/components/v2/Tag/PriorityLevelTag';
import { HRTicketStatusTag } from 'src/components/v2/Tag/TicketStatusTag/TicketStatusTag';
import urlPath from 'src/constants/urlPath';
import { AppContext } from 'src/contexts/app.context';
import { HrTicketDetailContext } from 'src/contexts/hrTicketDetail.context';
import { TicketContext } from 'src/contexts/ticket.context';
import { FlatItem } from 'src/types/elements.type';
import { IEquipmentSupplyRequestTicketDetail } from 'src/types/hr_ticket/equipmentSupplyRequestTicket.type';
import { formatDate } from 'src/utils/utils';
import './EquipmentSupplyRequestTicketDetail.scss';
const { Title, Text } = Typography;

export default function EquipmentSupplyRequestTicketDetail() {
  const { setInvalidKey } = useContext(TicketContext);
  const { setEquipmentSupplyRequestTicket, setLoading } = useContext(HrTicketDetailContext);
  const { ticketSelected } = useContext(AppContext);
  const { id } = useParams();

  const { useBreakpoint } = Grid;
  const breakpoints = useBreakpoint();
  const colspan = breakpoints.xxl ? 2 : 1;

  const equipmentSupplyRequestTicketQuery = useQuery({
    queryKey: ['equipmentSupplyRequestTicket', id],
    queryFn: () => getEquipmentSupplyRequestTicket(Number(id)),
    enabled: !!id && !!ticketSelected
  });

  useEffect(() => {
    if (equipmentSupplyRequestTicketQuery.data) {
      setEquipmentSupplyRequestTicket(equipmentSupplyRequestTicketQuery.data);
      setLoading(equipmentSupplyRequestTicketQuery.isFetching);
      setInvalidKey(JSON.stringify(equipmentSupplyRequestTicketQuery.data?.data));
    }
  }, [
    equipmentSupplyRequestTicketQuery.data,
    equipmentSupplyRequestTicketQuery.isFetching,
    setEquipmentSupplyRequestTicket,
    setInvalidKey,
    setLoading
  ]);

  const equipmentSupplyRequestTicketDetailItems = (
    equipmentSupplyRequestTicketDetails: IEquipmentSupplyRequestTicketDetail[]
  ): CollapseProps['items'] => {
    return equipmentSupplyRequestTicketDetails.map((equipmentSupplyRequestTicketDetail) => ({
      key: equipmentSupplyRequestTicketDetail.id,
      label: (
        <Descriptions
          className='equipment-supply-request-ticket-header'
          layout='horizontal'
          labelStyle={{ width: '135px' }}
          column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 2 }}
        >
          <Descriptions.Item label='Mã dự án' span={colspan}>
            {equipmentSupplyRequestTicketDetail.projectCode}
          </Descriptions.Item>
          <Descriptions.Item label='Mã thiết bị' span={colspan}>
            {equipmentSupplyRequestTicketDetail.asset?.assetCode}
          </Descriptions.Item>
          <Descriptions.Item label='Tên thiết bị' span={colspan}>
            {equipmentSupplyRequestTicketDetail.freeText}
          </Descriptions.Item>
          <Descriptions.Item label='Số lượng' span={colspan}>
            {equipmentSupplyRequestTicketDetail.quantity || 0} {equipmentSupplyRequestTicketDetail.uoM}
          </Descriptions.Item>
        </Descriptions>
      ),
      children: (
        <Descriptions
          className='equipment-supply-request-ticket-header'
          layout='horizontal'
          labelStyle={{ width: '135px' }}
          column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 2 }}
        >
          <Descriptions.Item label='Bộ phận sử dụng' span={colspan}>
            {equipmentSupplyRequestTicketDetail.department.name}
          </Descriptions.Item>
          <Descriptions.Item label='Ghi chú' span={colspan}>
            {equipmentSupplyRequestTicketDetail.note}
          </Descriptions.Item>
        </Descriptions>
      ),
      className: 'equipment-supply-request-ticket-item'
    }));
  };

  return (
    <div className='equipment-supply-request-ticket-detail-wrapper'>
      <BtnHrHandle type='equipmentSupplyRequestTicket' />

      <Divider style={{ marginTop: 16, marginBottom: 16 }} />

      <Fragment>
        <Flex justify='start'>
          <Title level={5}>Thông tin phiếu</Title>
        </Flex>
        {equipmentSupplyRequestTicketQuery.isFetching ? (
          <Skeleton />
        ) : (
          <Fragment>
            <Descriptions
              layout='horizontal'
              labelStyle={{ width: '135px' }}
              column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 2 }}
            >
              <Descriptions.Item label='Mã phiếu'>
                <Text strong>{equipmentSupplyRequestTicketQuery.data?.data?.code}</Text>
              </Descriptions.Item>
              <Descriptions.Item label='Trạng thái'>
                <HRTicketStatusTag ticketStatus={equipmentSupplyRequestTicketQuery.data?.data.ticketStatus} />
              </Descriptions.Item>
              <Descriptions.Item label='Ngày tạo' span={colspan}>
                <Text>{formatDate(equipmentSupplyRequestTicketQuery.data?.data?.createdDate)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label='Người tạo'>
                <Text>{equipmentSupplyRequestTicketQuery.data?.data?.createdBy?.fullName}</Text>
              </Descriptions.Item>
              <Descriptions.Item label='Bộ phận'>
                <Text>{equipmentSupplyRequestTicketQuery.data?.data?.createdBy?.department?.name}</Text>
              </Descriptions.Item>
              <Descriptions.Item label='Ngày cần' span={colspan}>
                <Text>{formatDate(equipmentSupplyRequestTicketQuery.data?.data?.requiredDate)}</Text>
              </Descriptions.Item>
              <Descriptions.Item label='Ticket raise' span={colspan}>
                {equipmentSupplyRequestTicketQuery.data?.data?.equipmentSupplyRequestTicketRefs?.map((item, index) => (
                  <Text key={index}>
                    {index > 0 && ', '}
                    <Link to={`${urlPath.myTicket.location}/${item.refTicketId}`}>{item?.ticket?.code}</Link>
                  </Text>
                ))}
              </Descriptions.Item>
              <Descriptions.Item label='Mức độ quan trọng' span={colspan}>
                <PriorityLevelTag
                  priorityLevel={equipmentSupplyRequestTicketQuery.data?.data?.priorityLevel as FlatItem}
                />
              </Descriptions.Item>
              <Descriptions.Item label='Ghi chú' span={colspan}>
                <Text strong italic>
                  {equipmentSupplyRequestTicketQuery.data?.data?.note}
                </Text>
              </Descriptions.Item>
            </Descriptions>
            <Divider style={{ marginTop: 16, marginBottom: 16 }} />
            <CollapseCustom
              title='Danh sách item'
              titleLevel={5}
              items={equipmentSupplyRequestTicketDetailItems(
                equipmentSupplyRequestTicketQuery.data?.data.equipmentSupplyRequestTicketDetails || []
              )}
              expandIconPosition='end'
              className='equipment-supply-request-ticket-list'
            />
          </Fragment>
        )}
      </Fragment>
    </div>
  );
}
